import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import AddTeacherDialogGlobal from './AddTeacherDialogGlobal';
import EditTeacherDialogGlobal from './EditTeacherDialogGlobal';
import './FacultyMembers.css';

const FacultyMembers = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({
    facultyName: '',
    departmentId: '',
    dept_ref: '',
    designation: '',
    name: '',
    email: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [, setTotalTeachers] = useState(0);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const fetchTeachers = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Build query parameters from filters
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          queryParams.append(key, value.trim());
        }
      });

      const response = await api.get(`/teachers?${queryParams.toString()}`);
      if (response.data.success) {
        setTeachers(response.data.data);
        setTotalTeachers(response.data.total || response.data.data.length);
      } else {
        setTeachers([]);
        setTotalTeachers(0);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Unauthorized. Please log in again.');
      } else {
        setError('Error loading teachers. Please try again.');
      }
      console.error('Error fetching teachers:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTeachers();
  }, [filters, fetchTeachers]);

  useEffect(() => {
    fetchFaculties();
  }, []);

  const fetchFaculties = async () => {
    try {
      const response = await api.get('/faculties');
      if (response.data.success) {
        setFaculties(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch faculties:', err);
    }
  };

  const fetchDepartments = async (facultyId) => {
    if (!facultyId) {
      setDepartments([]);
      return;
    }
    try {
      const response = await api.get(`/departments?faculty_ref=${facultyId}`);
      if (response.data.success) {
        setDepartments(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch departments:', err);
      setDepartments([]);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearchTerm(searchTerm.trim());
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));

    // If faculty is selected, fetch departments for that faculty
    if (key === 'facultyName' && value) {
      const selectedFaculty = faculties.find(f => f.name === value);
      if (selectedFaculty) {
        fetchDepartments(selectedFaculty._id);
        // Clear department filters when faculty changes
        setFilters(prev => ({
          ...prev,
          dept_ref: '',
          departmentId: ''
        }));
      }
    }
  };

  const clearFilters = () => {
    setFilters({
      facultyName: '',
      dept_ref: '',
      designation: '',
      name: '',
      email: ''
    });
    setSearchTerm('');
    setAppliedSearchTerm('');
    setDepartments([]);
  };

  const filteredTeachers = teachers.filter((teacher) => {
    if (!appliedSearchTerm) {
      return true;
    }

    const term = appliedSearchTerm.toLowerCase();
    return (
      teacher.name?.toLowerCase().includes(term) ||
      teacher.email?.toLowerCase().includes(term)
    );
  });

  // Teacher CRUD handlers
  const handleAddClick = () => {
    setIsAddDialogOpen(true);
  };

  const handleAddClose = () => {
    setIsAddDialogOpen(false);
  };

  const handleAddSave = (newTeacher) => {
    setTeachers(prev => [newTeacher, ...prev]);
    setTotalTeachers(prev => prev + 1);
  };

  const handleEditClick = (teacher) => {
    setEditingTeacher(teacher);
    setIsEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setIsEditDialogOpen(false);
    setEditingTeacher(null);
  };

  const handleEditSave = (updatedTeacher) => {
    setTeachers(prev => {
      const updated = prev.map(teacher =>
        teacher._id === updatedTeacher._id ? updatedTeacher : teacher
      );
      
      // Re-sort to maintain proper order after edit (by priority if available, otherwise by name)
      return updated.sort((a, b) => {
        if (a.priority !== undefined && b.priority !== undefined) {
          return a.priority - b.priority;
        }
        return a.name.localeCompare(b.name);
      });
    });
  };

  const handleDeleteTeacher = async (teacherId) => {
    if (window.confirm('Are you sure you want to delete this teacher?')) {
      try {
        const response = await api.delete(`/teachers/${teacherId}`);
        if (response.data.success) {
          setTeachers(prev => prev.filter(teacher => teacher._id !== teacherId));
          setTotalTeachers(prev => prev - 1);
        } else {
          alert('Failed to delete teacher: ' + response.data.message);
        }
      } catch (err) {
        alert('Error deleting teacher: ' + (err.response?.data?.message || err.message));
      }
    }
  };



  if (loading) {
    return (
      <div className="faculty-members-loading">
        <div className="loading-spinner"></div>
        <p>Loading faculty members...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="faculty-members-error">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={fetchTeachers} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="faculty-members">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1>All Faculty Members</h1>
            <p>{filteredTeachers.length} teacher{filteredTeachers.length !== 1 ? 's' : ''} found</p>
          </div>
          <button className="add-teacher-button" onClick={handleAddClick}>
            <span className="button-icon">+</span> Add New Teacher
          </button>
        </div>
      </div>

      <div className="search-filters-section">
        <div className="search-bar">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-group">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <button type="submit" className="search-button">
                <span className="search-icon">🔍</span>
                Search
              </button>
            </div>
          </form>
          
          <button 
            className={`filter-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <span className="filter-icon">⚙️</span>
            {showFilters ? 'Hide' : 'Show'} Filters
          </button>
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filters-grid">
              <div className="filter-group">
                <label htmlFor="facultyName">Faculty</label>
                <select
                  id="facultyName"
                  value={filters.facultyName}
                  onChange={(e) => handleFilterChange('facultyName', e.target.value)}
                >
                  <option value="">All Faculties</option>
                  {faculties.map((faculty) => (
                    <option key={faculty._id} value={faculty.name}>
                      {faculty.name}
                    </option>
                  ))}
                </select>
              </div>

              {filters.facultyName && (
                <div className="filter-group">
                  <label htmlFor="dept_ref">Department</label>
                  <select
                    id="dept_ref"
                    value={filters.dept_ref}
                    onChange={(e) => handleFilterChange('dept_ref', e.target.value)}
                  >
                    <option value="">All Departments</option>
                    {departments.map((dept) => (
                      <option key={dept._id} value={dept._id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}



              <div className="filter-group">
                <label htmlFor="designation">Designation</label>
                <select
                  id="designation"
                  value={filters.designation}
                  onChange={(e) => handleFilterChange('designation', e.target.value)}
                >
                  <option value="">All Designations</option>
                  <option value="চেয়ারম্যান">চেয়ারম্যান</option>
                  <option value="অধ্যাপক">অধ্যাপক</option>
                  <option value="সহযোগী অধ্যাপক">সহযোগী অধ্যাপক</option>
                  <option value="সহকারী অধ্যাপক">সহকারী অধ্যাপক</option>
                  <option value="প্রভাষক">প্রভাষক</option>
                  <option value="সেকশন অফিসার">সেকশন অফিসার</option>
                </select>
              </div>


            </div>

            <div className="filter-actions">
              <button onClick={clearFilters} className="clear-filters-btn">
                Clear All Filters
              </button>
              <button onClick={fetchTeachers} className="apply-filters-btn">
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="teachers-section">
        {filteredTeachers.length > 0 ? (
          <div className="teachers-grid">
            {filteredTeachers.map((teacher) => (
              <div key={teacher._id} className="teacher-card">
                                        <div className="teacher-header">
                          <div className="teacher-avatar">
                            {teacher.dp ? (
                              <img 
                                src={teacher.dp} 
                                alt={teacher.name} 
                                className="avatar-image"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <span 
                              className="avatar-text"
                              style={{ display: teacher.dp ? 'none' : 'flex' }}
                            >
                              {teacher.name.charAt(0)}
                            </span>
                          </div>
                        </div>
                
                <div className="teacher-content">
                  <h3 className="teacher-name">{teacher.name}</h3>
                  {teacher.nameEn && (
                    <p className="teacher-name-en">{teacher.nameEn}</p>
                  )}
                  <p className="teacher-designation">{teacher.designation}</p>
                  <p className="teacher-email">{teacher.email}</p>
                  {teacher.phoneNumber && (
                    <p className="teacher-phone">{teacher.phoneNumber}</p>
                  )}
                                            <div className="teacher-details">
                            <p className="teacher-faculty">
                              <strong>Faculty:</strong> {teacher.facultyName || 'N/A'}
                            </p>
                            <p className="teacher-department">
                              <strong>Department:</strong> {teacher.dept_ref?.name || 'N/A'}
                            </p>
                          </div>
                          <div className="teacher-actions">
                            <button 
                              className="edit-teacher-btn" 
                              onClick={() => handleEditClick(teacher)}
                            >
                              Edit
                            </button>
                            <button 
                              className="delete-teacher-btn" 
                              onClick={() => handleDeleteTeacher(teacher._id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
        ) : (
          <div className="no-teachers">
            <div className="empty-state">
              <div className="empty-icon">👨‍🏫</div>
              <h3>No Teachers Found</h3>
              <p>Try adjusting your search criteria or filters.</p>
              <button onClick={clearFilters} className="clear-search-btn">
                Clear Search & Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Teacher Dialog */}
      <AddTeacherDialogGlobal
        isOpen={isAddDialogOpen}
        onClose={handleAddClose}
        onSave={handleAddSave}
        faculties={faculties}
        departments={departments}
      />

      {/* Edit Teacher Dialog */}
      <EditTeacherDialogGlobal
        teacher={editingTeacher}
        isOpen={isEditDialogOpen}
        onClose={handleEditClose}
        onSave={handleEditSave}
        faculties={faculties}
        departments={departments}
      />
    </div>
  );
};

export default FacultyMembers;
